import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { SERVER_URL } from "./config";
import "./WorkersCheckinPage.css";

function joinUrl(base, path) {
  const b = (base || "").replace(/\/+$/, "");
  const p = (path || "").replace(/^\/+/, "");
  return `${b}/${p}`;
}

function getAuthHeader() {
  const authToken = localStorage.getItem("token"); // זה הטוקן של ההתחברות שלך (TokenAuth)
  return authToken ? { Authorization: `Token ${authToken}` } : {};
}

async function postJson(path, body) {
  const res = await fetch(joinUrl(SERVER_URL, path), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeader(),
    },
    body: JSON.stringify(body),
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    const msg =
      (data && (data.error || data.detail)) ||
      (res.status === 401 ? "אין הרשאות (התחברות/טוקן לא תקין)" : `Request failed (${res.status})`);
    throw new Error(msg);
  }

  return data;
}

export default function WorkersCheckinPage() {
  const { eventToken } = useParams();

  const [error, setError] = useState("");

  const token = useMemo(() => {
    return eventToken || localStorage.getItem("event_checkin_token") || "";
  }, [eventToken]);

  useEffect(() => {
    if (eventToken) {
      localStorage.setItem("event_checkin_token", eventToken);
    }
  }, [eventToken]);

  useEffect(() => {
    const authToken = localStorage.getItem("token");
    if (!authToken) setError("אין טוקן התחברות ב-localStorage תחת key: token");
    else if (!token) setError("אין טוקן אירוע. היכנסי דרך /workers/<event_uuid>");
    else setError("");
  }, [token]);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);

  const [tablesOpen, setTablesOpen] = useState(false);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [tablesData, setTablesData] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addArriving, setAddArriving] = useState("1");
  const [addTableNumber, setAddTableNumber] = useState("");
  const [addLoading, setAddLoading] = useState(false);

  const debounceRef = useRef(null);

  useEffect(() => {
    setError("");
    const q = query.trim();

    if (!token || !q) {
      setResults([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoadingSearch(true);
        const data = await postJson("wsearch", { token, q });
        setResults(Array.isArray(data) ? data : []);
      } catch (e) {
        setError(e.message || "שגיאה בחיפוש");
      } finally {
        setLoadingSearch(false);
      }
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, token]);

  async function toggleCheckIn(invited) {
    setError("");
    if (!token) return;

    try {
      const endpoint = invited.checked_in ? "wuncheckin" : "wcheckin";
      await postJson(endpoint, { token, invited_id: invited.id });

      setResults((prev) =>
        prev.map((x) =>
          x.id === invited.id ? { ...x, checked_in: !invited.checked_in } : x
        )
      );

      if (tablesOpen) {
        await loadTables();
      }
    } catch (e) {
      setError(e.message || "שגיאה בעדכון הגעה");
    }
  }

  async function loadTables() {
    setError("");
    if (!token) return;

    try {
      setTablesLoading(true);
      const data = await postJson("wtables", { token });
      setTablesData(data || {});
      setTablesOpen(true);
    } catch (e) {
      setError(e.message || "שגיאה בטעינת שולחנות");
    } finally {
      setTablesLoading(false);
    }
  }

  async function addInvited(e) {
    e.preventDefault();
    setError("");
    if (!token) return;

    const name = addName.trim();
    const arriving = addArriving.trim();
    const table_number = addTableNumber.trim();

    if (!name || !arriving || !table_number) {
      setError("חובה למלא: שם, כמה הגיעו, מספר שולחן");
      return;
    }

    const arrivingInt = Number(arriving);
    const tableInt = Number(table_number);

    if (!Number.isInteger(arrivingInt) || arrivingInt <= 0) {
      setError("כמה הגיעו חייב להיות מספר שלם גדול מ-0");
      return;
    }
    if (!Number.isInteger(tableInt) || tableInt <= 0) {
      setError("מספר שולחן חייב להיות מספר שלם גדול מ-0");
      return;
    }

    try {
      setAddLoading(true);
      const created = await postJson("wadd", {
        token,
        name,
        arriving: arrivingInt,
        table_number: tableInt,
      });

      if (created && created.id) {
        setResults((prev) => [
          {
            id: created.id,
            name: created.name,
            phon_number: created.phon_number,
            table_number: created.table_number,
            checked_in: created.checked_in,
          },
          ...prev,
        ]);
      }

      setAddName("");
      setAddArriving("1");
      setAddTableNumber("");
      setAddOpen(false);

      if (tablesOpen) {
        await loadTables();
      }
    } catch (e2) {
      setError(e2.message || "שגיאה בהוספת מוזמן");
    } finally {
      setAddLoading(false);
    }
  }

  const tableRows = useMemo(() => {
    if (!tablesData) return [];
    return Object.entries(tablesData)
      .map(([tableId, info]) => ({ tableId: Number(tableId), ...info }))
      .sort((a, b) => a.tableId - b.tableId);
  }, [tablesData]);

  const totals = useMemo(() => {
    return tableRows.reduce(
      (acc, t) => {
        acc.assigned += Number(t.assigned ?? 0);
        acc.arrived += Number(t.arrived ?? 0);
        acc.free += Number(t.free ?? 0);
        return acc;
      },
      { assigned: 0, arrived: 0, free: 0 }
    );
  }, [tableRows]);

  return (
    <div className="wk-page" dir="rtl">
      <div className="wk-header">
        <h2 className="wk-title">עמוד עובדים — צ׳ק אין</h2>
        <div className="wk-subtitle">
          אירוע: <span className="wk-mono">{token || "-"}</span>
        </div>
      </div>

      {error ? <div className="wk-error">{error}</div> : null}

      <div className="wk-controls">
        <input
          className="wk-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="חיפוש לפי שם או טלפון..."
        />

        <button className="wk-btn" onClick={loadTables} disabled={tablesLoading || !token}>
          {tablesLoading ? "טוען..." : "הצג שולחנות"}
        </button>

        <button className="wk-btn" onClick={() => setAddOpen((v) => !v)} disabled={!token}>
          הוספת מוזמן
        </button>
      </div>

      {addOpen && (
        <form className="wk-add" onSubmit={addInvited}>
          <div className="wk-add-row">
            <input
              className="wk-input"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              placeholder="שם מלא"
            />
            <input
              className="wk-input wk-input-small"
              value={addArriving}
              onChange={(e) => setAddArriving(e.target.value)}
              placeholder="כמה הגיעו"
              inputMode="numeric"
            />
            <input
              className="wk-input wk-input-small"
              value={addTableNumber}
              onChange={(e) => setAddTableNumber(e.target.value)}
              placeholder="מספר שולחן"
              inputMode="numeric"
            />
            <button className="wk-btn" type="submit" disabled={addLoading}>
              {addLoading ? "שומר..." : "שמור"}
            </button>
          </div>
        </form>
      )}

      <div className="wk-card">
        <div className="wk-card-head">
          <strong>תוצאות</strong>
          {loadingSearch ? <span className="wk-muted">מחפש...</span> : null}
        </div>

        {results.length === 0 ? (
          <div className="wk-empty">אין תוצאות</div>
        ) : (
          <div className="wk-results">
            {results.map((inv) => (
              <div className="wk-item" key={inv.id}>
                <div className="wk-item-main">
                  <div className="wk-item-name">{inv.name}</div>
                  <div className="wk-item-sub">
                    טלפון: {inv.phon_number ?? "-"} | שולחן: {inv.table_number ?? "-"}
                  </div>
                </div>

                <button className="wk-btn wk-btn-wide" onClick={() => toggleCheckIn(inv)} disabled={!token}>
                  {inv.checked_in ? "בטל הגעה" : "סמן הגיע"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {tablesOpen && (
        <div className="wk-card wk-mt">
          <div className="wk-card-head wk-card-head-row">
            <strong>שולחנות</strong>
            <div className="wk-tables-summary">
              סה״כ הגיעו: <strong>{totals.arrived}</strong>
            </div>
            <div className="wk-actions">
              <button className="wk-btn" onClick={loadTables} disabled={tablesLoading || !token}>
                רענן
              </button>
              <button className="wk-btn" onClick={() => setTablesOpen(false)}>
                סגור
              </button>
            </div>
          </div>

          <div className="wk-table-wrap">
            <table className="wk-table">
              <thead>
                <tr>
                  <th>שולחן</th>
                  <th>משוייכים</th>
                  <th>הגיעו</th>
                  <th>פנוי</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((t) => (
                  <tr key={t.tableId}>
                    <td>
                      {t.tableId}
                      {t.table_name ? ` - ${t.table_name}` : ""}
                    </td>
                    <td>{t.assigned ?? 0}</td>
                    <td>{t.arrived ?? 0}</td>
                    <td>{t.free ?? 0}</td>
                  </tr>
                ))}
                {tableRows.length === 0 && (
                  <tr>
                    <td colSpan="4" className="wk-empty-td">
                      אין נתונים
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
